import { useQuery } from "@tanstack/react-query";
import { addDays, startOfDay } from "date-fns";

import { supabase } from "../../../data/supabase/client";

const ACTIVE_STATUSES = ["generating", "created"] as const;

export interface ExistingArtifactMatch {
  id: string;
  agent_email: string;
  status: string;
}

interface FindExistingCoachingArtifactsParams {
  tenantId: string;
  agentEmail: string;
  lensName: string;
  weekStart: Date;
  weekEnd: Date;
}

export const findExistingCoachingArtifacts = async (
  params: FindExistingCoachingArtifactsParams
): Promise<ExistingArtifactMatch[]> => {
  const { tenantId, agentEmail, lensName, weekStart, weekEnd } = params;

  const startDayBegin = startOfDay(weekStart).toISOString();
  const startDayEnd = addDays(startOfDay(weekStart), 1).toISOString();
  const endDayBegin = startOfDay(weekEnd).toISOString();
  const endDayEnd = addDays(startOfDay(weekEnd), 1).toISOString();

  const { data, error } = await supabase
    .from("coaching_artifact")
    .select("id, agent_email, status")
    .eq("tenant_id", tenantId)
    .eq("lens_name", lensName)
    .eq("agent_email", agentEmail.toLowerCase())
    .in("status", ACTIVE_STATUSES)
    .is("deleted", null)
    .gte("start_date", startDayBegin)
    .lt("start_date", startDayEnd)
    .gte("end_date", endDayBegin)
    .lt("end_date", endDayEnd);

  if (error) {
    throw error;
  }

  return (data ?? []) as ExistingArtifactMatch[];
};

interface UseExistingCoachingArtifactsParams {
  tenantId: string | null;
  agentEmail: string;
  lensName: string;
  weekStart: Date;
  weekEnd: Date;
  enabled?: boolean;
}

export const useExistingCoachingArtifacts = ({
  tenantId,
  agentEmail,
  lensName,
  weekStart,
  weekEnd,
  enabled = true
}: UseExistingCoachingArtifactsParams) => {
  const normalizedEmail = agentEmail.toLowerCase();
  const weekStartKey = startOfDay(weekStart).toISOString();
  const weekEndKey = startOfDay(weekEnd).toISOString();

  return useQuery<ExistingArtifactMatch[], Error>({
    queryKey: [
      "coaching-artifacts-existing",
      tenantId,
      lensName,
      weekStartKey,
      weekEndKey,
      normalizedEmail
    ],
    enabled: enabled
      && !!tenantId
      && !!lensName
      && !!agentEmail,
    queryFn: () => findExistingCoachingArtifacts({
      tenantId: tenantId as string,
      agentEmail,
      lensName,
      weekStart,
      weekEnd
    })
  });
};
